import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Input, Select } from '@dreamtree-org/twreact-ui';

/**
 * Company / branch settings, rendered from the server's definition registry — the UI never
 * invents a key. Each value shows where it comes from (default, company, branch override).
 * Secrets are write-only: the field starts empty, "keep" is the default, and a masked
 * placeholder can never be submitted (the server rejects it anyway).
 *
 * Shared by the platform console (platform/CompanySettingsTab.jsx) and the company's own
 * settings screen (pages/admin/SettingsAdmin.jsx); each passes its own `source`:
 *   definitions()            → [{key, scope, valueType, isSecret, options, min, max}]
 *   load(branchId|null)      → {settings, secrets, sources, version}
 *   save(branchId|null, writes, expectedVersion)
 *   errorMessage(ex)         → string
 * `branches` = [{id, code}] for the scope picker; `children` renders under the toolbar.
 */

function readNested(obj, dotted) {
  return dotted.split('.').reduce((node, seg) => (node && Object.prototype.hasOwnProperty.call(node, seg) ? node[seg] : undefined), obj);
}

function groupOf(key) {
  return key.split('.')[0];
}

const BOOLEAN_OPTIONS = [{ value: 'true', label: 'On' }, { value: 'false', label: 'Off' }];

function coerce(def, raw) {
  if (def.valueType === 'number') return raw === '' ? '' : Number(raw);
  if (def.valueType === 'boolean') return raw === 'true';
  return raw;
}

function FieldLabel({ def, source }) {
  return (
    <span className="flex items-center gap-2">
      <code className="text-xs">{def.key}</code>
      <Badge size="sm" variant={source === 'branch' ? 'primary' : 'secondary'}>{source}</Badge>
    </span>
  );
}

function SecretField({ def, source, meta, pending, onChange }) {
  const replacing = pending && pending.op === 'replace';
  const clearing = pending && pending.op === 'clear';
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium"><FieldLabel def={def} source={source} /></div>
      <Input
        type="password"
        autoComplete="new-password"
        placeholder={meta.configured ? 'Configured — leave empty to keep' : 'Not set'}
        value={replacing ? pending.value : ''}
        onChange={(e) => onChange(e.target.value ? { key: def.key, op: 'replace', value: e.target.value } : null)}
      />
      {meta.configured && (
        <Button size="sm" variant="secondary" onClick={() => onChange({ key: def.key, op: 'clear' })}>
          {clearing ? 'Will be cleared on save' : 'Clear'}
        </Button>
      )}
    </div>
  );
}

function ValueField({ def, source, current, onChange }) {
  const label = <FieldLabel def={def} source={source} />;
  const emit = (raw) => onChange({ key: def.key, op: 'replace', value: coerce(def, raw) });
  if (def.valueType === 'boolean' || def.options) {
    const options = def.options ? def.options.map((o) => ({ value: o, label: o })) : BOOLEAN_OPTIONS;
    return <Select label={label} value={current == null ? '' : String(current)} onChange={emit} options={options} />;
  }
  return (
    <Input
      label={label}
      type={def.valueType === 'number' ? 'number' : 'text'}
      min={def.min ?? undefined}
      max={def.max ?? undefined}
      value={current == null ? '' : current}
      onChange={(e) => emit(e.target.value)}
    />
  );
}

export default function SettingsEditor({ source: backend, branches, children = null }) {
  const [definitions, setDefinitions] = useState([]);
  const [scope, setScope] = useState('');
  const [view, setView] = useState(null);
  const [draft, setDraft] = useState({});
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.resolve(backend.definitions()).then(setDefinitions).catch((ex) => setErr(backend.errorMessage(ex)));
  }, [backend]);

  const load = useCallback(async () => {
    try {
      setView(await backend.load(scope || null));
      setDraft({});
      setErr('');
    } catch (ex) {
      setErr(backend.errorMessage(ex));
    }
  }, [backend, scope]);

  useEffect(() => { load(); }, [load]);

  const scopeKind = scope ? 'branch' : 'company';
  const visible = useMemo(() => definitions.filter((d) => d.scope.includes(scopeKind) && d.exposure !== 'internal'), [definitions, scopeKind]);
  const groups = useMemo(() => [...new Set(visible.map((d) => groupOf(d.key)))], [visible]);

  const setPending = (key, write) => setDraft((d) => {
    const next = { ...d };
    if (write) next[key] = write;
    else delete next[key];
    return next;
  });

  const save = async () => {
    const writes = Object.values(draft);
    if (!writes.length) return;
    try {
      await backend.save(scope || null, writes, view.version);
      await load();
    } catch (ex) {
      setErr(backend.errorMessage(ex));
    }
  };

  if (!view) return err ? <Alert variant="error">{err}</Alert> : null;

  const renderField = (def) => {
    const source = view.sources[def.key] || 'default';
    const pending = draft[def.key];
    const onChange = (write) => setPending(def.key, write);
    if (def.isSecret) {
      return <SecretField key={def.key} def={def} source={source} meta={view.secrets[def.key] || { configured: false }} pending={pending} onChange={onChange} />;
    }
    const current = pending ? pending.value : readNested(view.settings, def.key);
    return <ValueField key={def.key} def={def} source={source} current={current} onChange={onChange} />;
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-64">
          <Select
            label="Scope"
            value={scope}
            onChange={(v) => setScope(v || '')}
            options={[{ value: '', label: 'Company default' }, ...branches.map((b) => ({ value: b.id, label: `Branch ${b.code} override` }))]}
          />
        </div>
        <p className="text-xs text-secondary-500 flex-1">
          {scope ? 'Branch overrides replace a provider profile (e.g. all of SMTP) as a whole. Login and recovery policy is company-wide only.' : 'Company values apply to every branch unless a branch overrides them.'}
        </p>
        <Button size="sm" onClick={save} disabled={!Object.keys(draft).length}>Save changes</Button>
      </div>
      {err && <Alert variant="error">{err}</Alert>}
      {children}
      {groups.map((group) => (
        <Card key={group} className="p-3 space-y-2">
          <h3 className="font-semibold capitalize">{group}</h3>
          <div className="grid gap-3 md:grid-cols-2">
            {visible.filter((d) => groupOf(d.key) === group).map(renderField)}
          </div>
        </Card>
      ))}
    </div>
  );
}
